import { useState } from "react";
import { ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "../i18n/LanguageContext";
import { IMAGES } from "../lib/images";
import { Reveal } from "../components/Reveal";
import { SEO } from "../components/SEO";
import { api } from "../lib/api";

const EMPTY = { name: "", email: "", phone: "", date: "", guests: "", eventType: "", location: "", budget: "", food: "", decoration: "", message: "" };

export default function PrivateEvents() {
  const { t, lang } = useLanguage();
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/inquiries", {
        type: "private_event", name: form.name, email: form.email, phone: form.phone, message: form.message,
        details: { date: form.date, guests: form.guests, eventType: form.eventType, location: form.location, budget: form.budget, food: form.food, decoration: form.decoration },
      });
      toast.success(t("privatePage.success"));
      setForm(EMPTY);
    } catch {
      toast.error(t("booking.bookingError"));
    } finally {
      setSending(false);
    }
  };

  const field = (key, label, type = "text", testid) => (
    <div>
      <label className="block text-xs uppercase tracking-[0.18em] text-earth mb-2">{label}</label>
      <input data-testid={testid} type={type} className="input-elegant"
        value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })} />
    </div>
  );

  return (
    <div data-testid="private-events-page">
      <SEO title="Private Events — Proposals, Birthdays & Bespoke Picnics, French Riviera | The Nice Picnic"
        description="Private picnics and bespoke events on the French Riviera: marriage proposals, birthdays, romantic moments, corporate events. Create your experience with The Nice Picnic." />

      <section className="relative min-h-[60svh] flex items-end pt-20">
        <div className="absolute inset-0">
          <img src={IMAGES.sunsetTable} alt="Romantic picnic for two under an olive tree — private events on the French Riviera" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#181513]/75 via-[#181513]/20 to-transparent" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 pb-16 w-full">
          <Reveal>
            <p className="overline-label !text-gold">{t("privatePage.overline")}</p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-ivory mt-4 max-w-3xl text-balance">{t("privatePage.title")}</h1>
          </Reveal>
        </div>
      </section>

      <section className="py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <Reveal className="lg:col-span-5">
            <p className="text-lg text-earth font-light leading-relaxed">{t("privatePage.intro")}</p>
            <div className="flex flex-wrap gap-3 mt-8">
              {t("privatePage.types").map((c) => (
                <span key={c} className="text-xs tracking-[0.12em] uppercase border border-champagne rounded-full px-4 py-2 text-earth">{c}</span>
              ))}
            </div>
            <div className="img-frame organic-crop mt-12 hidden lg:block">
              <img src={IMAGES.foodBoard} alt="Champagne toast over a Nice Picnic table by the water" className="w-full h-80 object-cover" loading="lazy" />
            </div>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-7">
            <form onSubmit={submit} className="border border-champagne rounded-3xl p-7 sm:p-10 bg-cream/60" data-testid="private-event-form">
              <h2 className="font-serif text-3xl text-ink mb-8">{t("privatePage.formTitle")}</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-xs uppercase tracking-[0.18em] text-earth mb-2">{t("privatePage.name")} *</label>
                  <input data-testid="pe-name" required className="input-elegant" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-[0.18em] text-earth mb-2">{t("privatePage.email")} *</label>
                  <input data-testid="pe-email" required type="email" className="input-elegant" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                </div>
                {field("phone", t("privatePage.phone"), "tel", "pe-phone")}
                {field("date", t("privatePage.date"), "date", "pe-date")}
                {field("guests", t("privatePage.guests"), "number", "pe-guests")}
                <div>
                  <label className="block text-xs uppercase tracking-[0.18em] text-earth mb-2">{t("privatePage.eventType")}</label>
                  <select data-testid="pe-event-type" className="input-elegant" value={form.eventType} onChange={(e) => setForm({ ...form, eventType: e.target.value })}>
                    <option value="">—</option>
                    {t("privatePage.types").map((ty) => <option key={ty} value={ty}>{ty}</option>)}
                  </select>
                </div>
                {field("location", t("privatePage.location"), "text", "pe-location")}
                {field("budget", t("privatePage.budget"), "text", "pe-budget")}
                {field("food", t("privatePage.food"), "text", "pe-food")}
                {field("decoration", t("privatePage.decoration"), "text", "pe-decoration")}
              </div>
              <div className="mt-5">
                <label className="block text-xs uppercase tracking-[0.18em] text-earth mb-2">{t("privatePage.message")}</label>
                <textarea data-testid="pe-message" rows={4} className="input-elegant" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
              </div>
              <button data-testid="pe-submit-btn" disabled={sending} className="btn-primary w-full mt-8 disabled:opacity-60">
                {t("privatePage.submit")} <ArrowRight size={16} />
              </button>
            </form>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
